import { useCallback, useEffect, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { IconClose, IconDownload } from "../../../components/Icons";
import { useAuth } from "../../../context/AuthContext";
import {
  ResizeHandle,
  useResizableDrawer,
} from "../../../hooks/useResizableDrawer";
import JsonViewer from "./JsonViewer";

type Tab = "tree" | "history";

type AnswerSection = {
  metadata: {
    carrier?: string;
    market?: string;
    version?: string | number;
  };
  answers: Array<{
    question_id?: number;
    question?: unknown;
    field?: string;
    value?: unknown;
    label?: string | null;
  }>;
};

type TreeResponse = {
  product: AnswerSection;
  medical: AnswerSection;
};

type HistoryRow = {
  created_at?: string;
  origin_app?: string;
  carrier_name?: string;
  version?: string;
  question_key?: string;
  translated_key?: string;
  answer_value?: string;
};

type HistoryResponse = {
  leadEmail: string | null;
  leadMarket: string;
  answers: HistoryRow[];
};

const VELOGICA_KEYS = new Set([
  "q313_leadcustomjson_velogica.title",
  "q313_leadcustomjson_squarelife.title",
]);

const fmtDateTime = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const parseMaybeJson = (raw: string | undefined | null): unknown => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

export default function AnswersExplorer({
  leadUlid,
  onClose,
}: {
  leadUlid: string;
  onClose: () => void;
}) {
  const { token } = useAuth();
  const [tab, setTab] = useState<Tab>("tree");
  const [tree, setTree] = useState<TreeResponse | null>(null);
  const [history, setHistory] = useState<HistoryResponse | null>(null);
  const [loadingTree, setLoadingTree] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchHistory, setSearchHistory] = useState("");
  const [downloading, setDownloading] = useState(false);
  const { width, startResize } = useResizableDrawer({
    initial: Math.min(900, Math.max(480, window.innerWidth - 80)),
    min: 480,
  });

  const loadTree = useCallback(async () => {
    setLoadingTree(true);
    setError(null);
    try {
      const data = await request<TreeResponse>(
        `/api/admin/operations/leads/${leadUlid}/answers/tree`,
        { token },
      );
      setTree(data);
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}`
          : "No se pudo cargar el tree",
      );
    } finally {
      setLoadingTree(false);
    }
  }, [leadUlid, token]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    setError(null);
    try {
      const data = await request<HistoryResponse>(
        `/api/admin/operations/leads/${leadUlid}/answers`,
        { token },
      );
      setHistory(data);
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}`
          : "No se pudo cargar el historial",
      );
    } finally {
      setLoadingHistory(false);
    }
  }, [leadUlid, token]);

  useEffect(() => {
    if (tab === "tree" && !tree && !loadingTree) loadTree();
    if (tab === "history" && !history && !loadingHistory) loadHistory();
  }, [tab, tree, history, loadingTree, loadingHistory, loadTree, loadHistory]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      // The tree is the source of truth for downloads — same shape as the legacy export.
      const data =
        tree ??
        (await request<TreeResponse>(
          `/api/admin/operations/leads/${leadUlid}/answers/tree`,
          { token },
        ));
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `answers-${leadUlid}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}`
          : "No se pudo descargar",
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 50,
        display: "flex",
        justifyContent: "flex-end",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          height: "100%",
          background: "#f8f9fb",
          boxShadow: "-2px 0 16px rgba(0,0,0,.18)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <ResizeHandle onMouseDown={startResize} />
        <header
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            padding: "0.85rem 1.1rem",
            background: "white",
            borderBottom: "1px solid #e9ecef",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: "1rem", color: "#1f2933" }}>
              Answers map
            </h2>
            <div style={{ fontSize: ".75rem", color: "#73879C" }}>
              Tree actual + historial completo del lead
            </div>
          </div>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            title="Descargar JSON del tree actual"
            style={downloadBtnStyle}
          >
            <IconDownload style={{ width: ".95rem", height: ".95rem" }} />
            <span>{downloading ? "Descargando…" : "Descargar JSON"}</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Cerrar"
            style={closeBtnStyle}
          >
            <IconClose />
          </button>
        </header>

        <div
          role="tablist"
          style={{
            display: "flex",
            gap: "0.25rem",
            padding: "0.5rem 1.1rem 0",
            background: "white",
            borderBottom: "1px solid #e9ecef",
          }}
        >
          <TabBtn active={tab === "tree"} onClick={() => setTab("tree")}>
            Tree actual
          </TabBtn>
          <TabBtn active={tab === "history"} onClick={() => setTab("history")}>
            Historial
          </TabBtn>
        </div>

        {error && (
          <div
            style={{
              padding: "0.6rem 1.1rem",
              background: "#fde2e2",
              color: "#b32424",
              fontSize: ".82rem",
            }}
          >
            {error}
          </div>
        )}

        <div style={{ flex: 1, overflow: "auto", padding: "1rem 1.1rem" }}>
          {tab === "tree" ? (
            <TreeView tree={tree} loading={loadingTree} />
          ) : (
            <HistoryView
              data={history}
              loading={loadingHistory}
              search={searchHistory}
              onSearch={setSearchHistory}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function TabBtn({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: "0.5rem 1rem",
        background: "transparent",
        color: active ? "#0069d9" : "#73879C",
        border: "none",
        borderBottom: `2px solid ${active ? "#0069d9" : "transparent"}`,
        fontSize: ".82rem",
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function TreeView({
  tree,
  loading,
}: {
  tree: TreeResponse | null;
  loading: boolean;
}) {
  if (loading && !tree) {
    return <div style={loadingStyle}>Cargando tree…</div>;
  }
  if (!tree) return null;
  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <AnswerSectionCard title="Product" section={tree.product} />
      <AnswerSectionCard title="Medical" section={tree.medical} />
    </div>
  );
}

function AnswerSectionCard({
  title,
  section,
}: {
  title: string;
  section: AnswerSection;
}) {
  const { metadata, answers } = section;
  const hasMetadata =
    metadata && (metadata.carrier || metadata.market || metadata.version);
  return (
    <section
      style={{
        background: "white",
        borderRadius: "0.55rem",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        borderLeft: "3px solid #0069d9",
        overflow: "hidden",
      }}
    >
      <header
        style={{
          padding: "0.65rem 0.95rem",
          background: "#f8f9fb",
          borderBottom: "1px solid #e9ecef",
        }}
      >
        <div
          style={{
            fontSize: ".82rem",
            fontWeight: 600,
            color: "#1f2933",
            textTransform: "uppercase",
            letterSpacing: ".4px",
          }}
        >
          {title}
        </div>
        {hasMetadata && (
          <div style={{ fontSize: ".72rem", color: "#73879C", marginTop: 2 }}>
            {metadata.carrier && <span>Carrier: {metadata.carrier} · </span>}
            {metadata.market && <span>Market: {metadata.market} · </span>}
            {metadata.version !== undefined && (
              <span>Version: {String(metadata.version)}</span>
            )}
          </div>
        )}
      </header>
      <div>
        {answers.length === 0 ? (
          <div style={emptyRowStyle}>Sin respuestas</div>
        ) : (
          answers.map((a, i) => (
            <AnswerRow key={`${a.question_id ?? a.field ?? i}-${i}`} answer={a} />
          ))
        )}
      </div>
    </section>
  );
}

function AnswerRow({
  answer,
}: {
  answer: AnswerSection["answers"][number];
}) {
  const questionText = renderQuestion(answer.question, answer.field);
  const isComplexValue =
    answer.value !== null &&
    typeof answer.value === "object" &&
    answer.value !== undefined;
  return (
    <div
      style={{
        padding: "0.6rem 0.95rem",
        borderTop: "1px solid #f1f3f5",
        fontSize: ".82rem",
      }}
    >
      <div style={{ color: "#1f2933", marginBottom: "0.2rem" }}>
        {questionText}
      </div>
      <div style={{ fontSize: ".72rem", color: "#73879C" }}>
        {answer.field && (
          <code style={{ background: "#f1f3f5", padding: "0 4px", borderRadius: 3 }}>
            {answer.field}
          </code>
        )}
        {answer.label && <span style={{ marginLeft: 6 }}>· {answer.label}</span>}
      </div>
      <div style={{ marginTop: "0.35rem" }}>
        {isComplexValue ? (
          <JsonViewer value={answer.value} initialDepth={0} />
        ) : (
          <span
            style={{
              display: "inline-block",
              padding: "0.15rem 0.45rem",
              background: "#e7f1ff",
              color: "#0a58ca",
              borderRadius: "0.3rem",
              fontSize: ".82rem",
              fontWeight: 600,
              fontFamily: "monospace",
            }}
          >
            {formatScalar(answer.value)}
          </span>
        )}
      </div>
    </div>
  );
}

function renderQuestion(question: unknown, field?: string): string {
  if (typeof question === "string" && question.trim() !== "") return question;
  if (question && typeof question === "object") {
    // QuestionTree content can be a localized record like { en: "...", es: "..." }.
    const obj = question as Record<string, unknown>;
    for (const v of Object.values(obj)) {
      if (typeof v === "string" && v.trim() !== "") return v;
    }
  }
  return field ?? "(sin enunciado)";
}

function formatScalar(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}

function HistoryView({
  data,
  loading,
  search,
  onSearch,
}: {
  data: HistoryResponse | null;
  loading: boolean;
  search: string;
  onSearch: (v: string) => void;
}) {
  if (loading && !data) {
    return <div style={loadingStyle}>Cargando historial…</div>;
  }
  if (!data) return null;
  const filtered = data.answers.filter((row) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (row.translated_key ?? "").toLowerCase().includes(q) ||
      (row.question_key ?? "").toLowerCase().includes(q) ||
      (row.answer_value ?? "").toLowerCase().includes(q)
    );
  });
  return (
    <div>
      <div style={{ marginBottom: "0.6rem" }}>
        <input
          type="text"
          value={search}
          placeholder="Buscar por pregunta o respuesta…"
          onChange={(e) => onSearch(e.target.value)}
          style={{
            width: "100%",
            padding: "0.45rem 0.65rem",
            border: "1px solid #dee2e6",
            background: "white",
            borderRadius: "0.4rem",
            fontSize: ".85rem",
          }}
        />
      </div>
      {filtered.length === 0 ? (
        <div style={emptyRowStyle}>
          {data.answers.length === 0
            ? "Este lead no tiene respuestas registradas"
            : "Ningún resultado para el filtro"}
        </div>
      ) : (
        <div
          style={{
            background: "white",
            borderRadius: "0.55rem",
            boxShadow: "0 1px 3px rgba(0,0,0,.04)",
            overflow: "hidden",
          }}
        >
          {filtered.map((row, i) => (
            <HistoryRow key={i} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryRow({ row }: { row: HistoryRow }) {
  const isVelogica = VELOGICA_KEYS.has(row.question_key ?? "");
  const parsed = isVelogica ? parseMaybeJson(row.answer_value) : null;
  return (
    <div
      style={{
        padding: "0.65rem 0.95rem",
        borderTop: "1px solid #f1f3f5",
        fontSize: ".82rem",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "0.7rem",
          flexWrap: "wrap",
          fontSize: ".72rem",
          color: "#73879C",
          marginBottom: "0.25rem",
        }}
      >
        <span>{fmtDateTime(row.created_at)}</span>
        {row.origin_app && (
          <span
            style={{
              padding: "0.05rem 0.4rem",
              borderRadius: 999,
              background: "#e9ecef",
              color: "#495057",
              fontWeight: 600,
            }}
          >
            {row.origin_app}
          </span>
        )}
        {row.carrier_name && (
          <span>
            {row.carrier_name}
            {row.version && `:${row.version}`}
          </span>
        )}
      </div>
      <div style={{ color: "#1f2933", marginBottom: "0.3rem" }}>
        {row.translated_key && row.translated_key !== row.question_key ? (
          <>
            {row.translated_key}
            <code
              style={{
                marginLeft: 6,
                background: "#f1f3f5",
                padding: "0 4px",
                borderRadius: 3,
                fontSize: ".7rem",
                color: "#73879C",
              }}
            >
              {row.question_key}
            </code>
          </>
        ) : (
          <code
            style={{
              background: "#f1f3f5",
              padding: "0 4px",
              borderRadius: 3,
            }}
          >
            {row.question_key}
          </code>
        )}
      </div>
      <div>
        {isVelogica && parsed !== null && typeof parsed === "object" ? (
          <JsonViewer value={parsed} initialDepth={0} />
        ) : (
          <span
            style={{
              fontFamily: "monospace",
              fontSize: ".82rem",
              color: "#1f2933",
              wordBreak: "break-all",
            }}
          >
            {row.answer_value ?? "—"}
          </span>
        )}
      </div>
    </div>
  );
}

const downloadBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.35rem",
  padding: "0.4rem 0.7rem",
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  fontSize: ".78rem",
  fontWeight: 600,
  cursor: "pointer",
};

const closeBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "2rem",
  height: "2rem",
  padding: 0,
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  cursor: "pointer",
};

const loadingStyle: React.CSSProperties = {
  padding: "1rem",
  color: "#73879C",
  fontSize: ".85rem",
  textAlign: "center",
};

const emptyRowStyle: React.CSSProperties = {
  padding: "0.65rem 0.95rem",
  color: "#73879C",
  fontSize: ".82rem",
};
